import { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Loader2, Search, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@stockline/ui/components/popover";
import { Button } from "@stockline/ui/components/button";
import { Input } from "@stockline/ui/components/input";
import { cn } from "@/lib/utils";
import { usePartner, usePartners } from "@/query/hooks/usePartners";
import { useDebounce } from "@/hooks/use-debounce";
import { filterAndSearchPartners } from "@/lib/search";
import type { Partner } from "@/app/api/types/partner";

type UUID = string;
type UUIDBrand = `${string}-${string}-${string}-${string}-${string}`;

/**
 * Resolve the partner row to render on the trigger button. Looks in the
 * (already-filtered) dropdown list first so an active selection paints
 * without an extra round-trip, then falls back to a direct fetch by id
 * — `usePartner` reads through Dexie + REST and returns the row even if
 * `is_active=false`, preserving the trigger label for orders pre-bound
 * to a deactivated customer. Extracted so the parent component stays
 * within SonarCloud's cognitive-complexity budget.
 */
function useSelectedPartner(
  value: UUID | null | undefined,
  partners: Partner[],
) {
  const selectedFromList = partners.find((p) => p.id === value);
  const needsFallback = !!value && !selectedFromList;
  const { data: selectedFallback } = usePartner(
    needsFallback ? (value as UUIDBrand) : null,
  );
  return selectedFromList ?? selectedFallback ?? undefined;
}

interface PartnerSelectProps {
  value: UUID | null | undefined;
  onChange: (partnerId: UUID | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  size?: "sm" | "default" | "lg";
  // Optional preloaded partners (shared across rows)
  partners?: Partner[];
  partnersLoading?: boolean;
}

/**
 * Optimized PartnerSelect component with:
 * - Lazy loading (only loads when dropdown opens)
 * - Infinite scroll pagination
 * - Search with debounce
 * - Handles 1000+ partners efficiently
 */
export function PartnerSelect({
  value,
  onChange,
  placeholder,
  disabled = false,
  className,
  size = "default",
  partners: injectedPartners,
  partnersLoading = false,
}: PartnerSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const observerTarget = useRef<HTMLDivElement>(null);

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Only fetch partners when dropdown is open (lazy loading).
  // If partners are injected, skip the hook entirely to share data across rows.
  const {
    data: partnersFromHook = [],
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePartners({
    search: debouncedSearchQuery,
    enabled: open && !injectedPartners,
    excludeInactive: true,
  });

  // Mirror the hook-side exclusion on the injected branch so callers that
  // share a single pre-fetched list (e.g. flow tables) still hide inactive
  // partners from this picker's DROPDOWN. The current `value` is resolved
  // separately via `usePartner(value)` below so an order already bound to
  // a now-inactive partner still renders its label on the trigger button —
  // we only refuse fresh selections, never break display of existing
  // bindings. CRM / pricing screens keep the broader hook default
  // (`excludeInactive: false`) since they need to surface stale rows.
  const partners = useMemo(
    () =>
      (injectedPartners ?? partnersFromHook).filter(
        (p) => p.is_active !== false,
      ),
    [injectedPartners, partnersFromHook],
  );

  const selectedPartner = useSelectedPartner(value, partners);

  const filteredPartners = useMemo(() => {
    if (!partners || partners.length === 0) return [];
    if (!injectedPartners) {
      return partners;
    }

    return debouncedSearchQuery
      ? filterAndSearchPartners(partners, { search: debouncedSearchQuery })
      : partners;
  }, [partners, debouncedSearchQuery, injectedPartners]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!open || injectedPartners) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "200px", // Load 200px before reaching bottom
      },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSelect = (partnerId: UUID) => {
    onChange(partnerId);
    setOpen(false);
    setSearchQuery("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setOpen(false);
    setSearchQuery("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="partner-select"
          className={cn(
            "justify-between",
            !value && "text-muted-foreground",
            className,
          )}
          size={size}
          disabled={disabled}
        >
          <span className="truncate">
            {selectedPartner?.usage_name ||
              selectedPartner?.name ||
              placeholder ||
              t("messageSettings.sms.selectPartner")}
          </span>
          {value ? (
            <X
              className="ml-2 h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
              onClick={handleClear}
            />
          ) : (
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="start"
        onWheel={(e) => e.stopPropagation()}
      >
        {!open ? null : (
          <div className="flex flex-col">
            {/* Search input */}
            <div className="border-b p-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("common.search")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-9"
                  autoFocus
                  data-testid="partner-search-input"
                />
              </div>
            </div>

            {/* Partner list */}
            <div
              className="max-h-64 overflow-y-auto"
              style={{ overscrollBehavior: "contain" }}
            >
              {partners.length === 0 && (partnersLoading || isLoading) ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : partners.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  {searchQuery.trim()
                    ? t("common.noResults")
                    : t("common.noData")}
                </div>
              ) : (
                <>
                  {filteredPartners.map((partner) => {
                    const isSelected = partner.id === value;
                    return (
                      <button
                        key={partner.id}
                        type="button"
                        data-testid="partner-option"
                        className={cn(
                          "flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-accent transition-colors",
                          isSelected && "bg-accent",
                        )}
                        onClick={() => handleSelect(partner.id as UUID)}
                      >
                        <Check
                          className={cn(
                            "h-4 w-4 shrink-0",
                            isSelected ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">
                            {partner.usage_name || partner.name}
                          </div>
                          {partner.code && (
                            <div className="text-sm text-muted-foreground truncate">
                              {partner.code}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}

                  {/* Sentinel element for infinite scroll */}
                  {hasNextPage && (
                    <div ref={observerTarget} className="p-2 text-center">
                      <Loader2 className="h-4 w-4 mx-auto animate-spin text-muted-foreground" />
                    </div>
                  )}

                  {isFetchingNextPage && !hasNextPage && (
                    <div className="p-2 text-center text-xs text-muted-foreground">
                      {t("common.loadingMore")}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
